import type { WorkbookDefinition, WorkbookDiagnostic } from "@erp/shared-types";
import {
  sheetsOnCycles,
  workbookDependencies,
  workbookExecutionOrder,
  type ExecutionItem,
} from "./executionOrder";
import type { FormulaTables } from "./formula";
import type { Schema, WorkbookPlan } from "./planTypes";
import { RESERVED_PREFIX, isReserved } from "./reservedNames";
import { sheetKind } from "./sheetKinds";
import { resolveTable, tableItems } from "./tableReferences";
import {
  planValidations,
  relationalValidationDiagnostics,
} from "./validations";

export type {
  PlannedColumn,
  PlannedField,
  PlannedLookup,
  Schema,
  SheetPlan,
  SheetStages,
  WorkbookPlan,
} from "./planTypes";

function sourceDiagnostics(
  sourceColumns: ReadonlyMap<string, readonly string[]>,
  definition: WorkbookDefinition,
): WorkbookDiagnostic[] {
  return definition.sources
    .filter((source) => (sourceColumns.get(source.id) ?? []).some(isReserved))
    .map((source) => ({
      code: "reserved-name",
      message: `Input column names beginning with ${RESERVED_PREFIX} are reserved.`,
      location: { part: "item", itemId: source.id },
    }));
}

/** Root causes of blocked Worksheets: a missing dependency, or a cycle. */
function dependencyDiagnostics(
  definition: WorkbookDefinition,
  order: readonly ExecutionItem[],
): WorkbookDiagnostic[] {
  const ids = new Set(
    [...definition.sources, ...definition.sheets].map((item) => item.id),
  );
  const blocked = new Set(
    order
      .filter((item) => item.kind === "sheet" && item.blocked)
      .map((item) => item.id),
  );
  const cycles = sheetsOnCycles(definition, blocked);
  const result: WorkbookDiagnostic[] = [];
  for (const sheet of definition.sheets) {
    result.push(...sheetKind(sheet).missingReads(ids));
    if (cycles.has(sheet.id))
      result.push({
        code: "sheet-cycle",
        message:
          "These worksheets use each other as input (a circular reference). A worksheet can't use itself, or a worksheet that uses it.",
        location: { part: "item", itemId: sheet.id },
      });
  }
  return result;
}

/** Table references resolved to their tables' schemas, as the planner knows them. */
function formulaTables(
  definition: WorkbookDefinition,
  schemas: ReadonlyMap<string, Schema>,
): FormulaTables {
  const items = tableItems(definition);
  return (table) => {
    const resolved = resolveTable(items, table);
    if (!resolved.ok) return resolved;
    const { id, name } = resolved.item;
    // A Worksheet is planned only once every table it reads has a schema.
    return { ok: true, table: { id, name, fields: schemas.get(id) ?? [] } };
  };
}

/**
 * Plans a Definition against Source column names without reading any rows.
 * Source columns are text by contract; a Worksheet's schema is its declared
 * output, so every Worksheet is planned even when it cannot run. A Source
 * missing from `sourceColumns` has an unknown schema: Worksheets reading it get
 * only the checks that need no schema.
 */
export function planWorkbook(
  definition: WorkbookDefinition,
  sourceColumns: ReadonlyMap<string, readonly string[]>,
): WorkbookPlan {
  const schemas = new Map<string, Schema>();
  for (const source of definition.sources) {
    const columns = sourceColumns.get(source.id);
    if (columns)
      schemas.set(
        source.id,
        columns.map((name) => ({ name, type: "text" })),
      );
  }
  for (const sheet of definition.sheets)
    schemas.set(sheet.id, sheetKind(sheet).declaredOutput());
  const dependencies = workbookDependencies(definition);
  const tables = formulaTables(definition, schemas);
  const items = tableItems(definition);
  const planned = definition.sheets.map((sheet) =>
    sheetKind(sheet).plan({
      schemas,
      tables,
      items,
      dependencies: dependencies.get(sheet.id) ?? [],
    }),
  );
  const order = workbookExecutionOrder(definition);
  const diagnostics = [
    ...sourceDiagnostics(sourceColumns, definition),
    ...dependencyDiagnostics(definition, order),
    ...planned.flatMap((sheet) => sheet.diagnostics),
    ...definition.sheets.flatMap((sheet) => planValidations(sheet).diagnostics),
    ...relationalValidationDiagnostics(definition.sheets),
  ];
  return {
    order,
    sheets: new Map(planned.map(({ plan }) => [plan.sheetId, plan])),
    tables: schemas,
    diagnostics,
    ok: diagnostics.length === 0,
  };
}

/**
 * Source column names known from the Definition alone: those of inline tables.
 * Every other Source needs a read to know its columns, so it is left out and
 * planned as an unknown schema.
 */
export function definitionSourceColumns(
  definition: WorkbookDefinition,
): Map<string, readonly string[]> {
  return new Map(
    definition.sources.flatMap((source) =>
      source.kind === "input" ? [[source.id, source.table.columns]] : [],
    ),
  );
}
